import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { StudentFocusSessionProvider, useStudentFocusSession } from '../contexts/StudentFocusSessionContext';
import StudentHeader from '../components/student/StudentHeader';
import {
  HomeIcon,
  LayoutDashboard,
  CalculatorIcon,
  ZapIcon,
  CreditCardIcon,
  TrophyIcon,
  ArchiveIcon,
  CalendarIcon,
  Target,
  Gamepad2,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import StudentCharacterImg from '../components/student/StudentCharacterImg';
import { PROJECT_CHARACTERS } from '../constants/projectCharacters';

const MASCOT_TIPS = [
  'dashboard.student.mascotTip1',
  'dashboard.student.mascotTip2',
  'dashboard.student.mascotTip3',
  'dashboard.student.mascotTip4',
];

const StudentLayoutInner = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { focusSession } = useStudentFocusSession();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64);
  const headerWrapRef = useRef(null);

  useLayoutEffect(() => {
    const el = headerWrapRef.current;
    if (!el) return;
    const measure = () => setHeaderHeight(el.offsetHeight);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (focusSession) setSidebarOpen(false);
  }, [focusSession]);

  const navigationItems = [
    { name: t('navigation.home'), href: '/student', icon: HomeIcon },
    { name: t('navigation.soroban'), href: '/student/soroban', icon: CalculatorIcon },
    { name: t('navigation.dailyExercises'), href: '/student/daily-exercises', icon: CalendarIcon },
    { name: t('navigation.flashAnzan'), href: '/student/flash-anzan', icon: ZapIcon },
    { name: t('navigation.flashCards'), href: '/student/flash-cards', icon: CreditCardIcon },
    { name: t('competitions.title'), href: '/student/competitions', icon: TrophyIcon },
    { name: t('competitions.archive'), href: '/student/competitions/archive', icon: ArchiveIcon },
    { name: t('navigation.challenges'), href: '/student/challenges', icon: Target },
    { name: t('navigation.games'), href: '/student/games', icon: Gamepad2 },
    { name: t('navigation.dashboard'), href: '/student/dashboard', icon: LayoutDashboard },
  ];

  const isNavActive = (href) => {
    const p = location.pathname.replace(/\/$/, '') || '';
    const h = href.replace(/\/$/, '');
    if (h === '/student') return p === '/student';
    if (h === '/student/competitions/archive') return p.includes('/competitions/archive');
    if (h === '/student/competitions') {
      return p.startsWith('/student/competitions') && !p.includes('/archive');
    }
    if (h === '/student/challenges') return p.startsWith('/student/challenges');
    if (h === '/student/games') return p.startsWith('/student/games');
    return p === h || p.startsWith(`${h}/`);
  };

  const mascotTip = MASCOT_TIPS[Math.floor(Math.random() * MASCOT_TIPS.length)];

  const toggleSidebar = () => setSidebarOpen((o) => !o);

  const sideFrom = isRTL ? 'right' : 'left';
  const translateClosed = isRTL ? 'translate-x-full' : '-translate-x-full';

  return (
    <div
      className={`min-h-screen w-full overflow-x-hidden ${
        focusSession ? 'bg-black' : 'bg-gradient-to-b from-sky-100/90 via-violet-50/40 to-amber-50/50'
      } ${isRTL ? 'font-arabic' : ''}`}
    >
      {!focusSession && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -right-24 w-[22rem] h-[22rem] rounded-full bg-fuchsia-300/25 blur-3xl" />
          <div className="absolute top-1/3 -left-32 w-80 h-80 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-amber-200/30 blur-3xl" />
        </div>
      )}

      {!focusSession && (
        <div ref={headerWrapRef} className="relative z-[60] w-full shrink-0">
          <StudentHeader onMenuClick={toggleSidebar} menuOpen={sidebarOpen} />
        </div>
      )}

      {!focusSession && sidebarOpen && (
        <button
          type="button"
          style={{ top: headerHeight }}
          className="fixed left-0 right-0 bottom-0 z-[50] bg-slate-900/35 backdrop-blur-[2px] cursor-default border-0 p-0"
          aria-label={t('dashboard.student.closeMenu')}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {!focusSession && (
        <aside
          style={{ top: headerHeight }}
          className={`fixed bottom-0 z-[55] w-[min(20rem,88vw)] max-w-full bg-white border-slate-900/10 shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
            sideFrom === 'left' ? 'left-0 border-r-[3px]' : 'right-0 border-l-[3px]'
          } ${sidebarOpen ? 'translate-x-0' : translateClosed}`}
          aria-hidden={!sidebarOpen}
        >
          <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto min-h-0">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isNavActive(item.href);
              return (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`flex items-center w-full px-4 py-3.5 text-base font-bold rounded-2xl border-[3px] transition-all min-h-[3rem] ${
                    isActive
                      ? 'bg-lime-200 border-slate-900/15 text-slate-900 shadow-[0_4px_0_0_rgba(15,23,42,0.12)]'
                      : 'bg-white border-slate-900/10 text-slate-800 hover:bg-sky-50 hover:border-sky-200'
                  } ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                >
                  <Icon className={`w-6 h-6 flex-shrink-0 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                  {item.name}
                </button>
              );
            })}
          </nav>

          <div className="p-3 border-t-[3px] border-slate-900/8 bg-violet-50/80">
            <div
              className={`flex items-start gap-3 p-3 rounded-2xl bg-white border-[3px] border-slate-900/10 ${
                isRTL ? 'flex-row-reverse' : ''
              }`}
            >
              <div className="w-14 h-14 rounded-xl bg-violet-100 border-[3px] border-slate-900/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <StudentCharacterImg
                  imageFile={PROJECT_CHARACTERS.boulot.imageFile}
                  alt={t(PROJECT_CHARACTERS.boulot.i18nAltKey)}
                  className="h-full w-full object-contain scale-110 translate-y-0.5"
                />
              </div>
              <div className={`min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                <p className="font-extrabold text-slate-900 text-sm">{t('dashboard.student.mascotName')}</p>
                <p className="text-xs text-slate-600 mt-1 leading-snug">{t(mascotTip)}</p>
              </div>
            </div>
          </div>
        </aside>
      )}

      <main
        className={
          focusSession
            ? 'fixed inset-0 z-[80] m-0 h-[100dvh] max-h-[100dvh] w-full min-h-0 overflow-hidden bg-black p-0'
            : 'relative z-0 w-full min-h-[calc(100vh-4rem)] px-3 sm:px-5 py-4 sm:py-6'
        }
      >
        <div className="w-full max-w-none mx-auto min-h-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

const StudentLayout = () => (
  <StudentFocusSessionProvider>
    <StudentLayoutInner />
  </StudentFocusSessionProvider>
);

export default StudentLayout;
